// Utility helpers for converting flat timesheet rows into slot-based
// structures used by both ClientDashboard and Admin Jobs Timesheets.
//
// A "slot" represents a single time range (scheduled start → scheduled end)
// at a specific location, with one or more position/staff rows underneath.

import {
  accountAvatarImgSrc,
  resolveBackendUrl,
} from "./accountAvatarUrl";
import { parseDbDateTime, getAppWallDateParts } from "./appDateTime";

function normalizePositionBaseLabel(raw) {
  return String(raw || "Role").trim() || "Role";
}

function comparePositionLabels(a, b) {
  return normalizePositionBaseLabel(a).localeCompare(
    normalizePositionBaseLabel(b),
    undefined,
    { sensitivity: "base", numeric: true },
  );
}

function normalizeStaffLabel(raw) {
  return String(raw || "").trim();
}

function isUnassignedStaffLabel(raw) {
  const label = normalizeStaffLabel(raw).toLowerCase();
  return !label || label === "unassigned";
}

function compareStaffLabels(a, b) {
  return normalizeStaffLabel(a).localeCompare(normalizeStaffLabel(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

function getSlotStaffLabel(slot) {
  const labels = (slot?.positions || [])
    .map((position) => position.staffLabel || position.staff || position.temp)
    .filter((label) => !isUnassignedStaffLabel(label));
  if (labels.length === 0) return "";
  return labels.sort(compareStaffLabels)[0];
}

/** API returns /api/avatars/... paths; prefix backend base for Netlify/production (no CRA proxy). */
function resolveStaffAvatarSrc(raw) {
  const legacy =
    raw?.avatar_url ||
    raw?.profile_picture ||
    raw?.staff_avatar ||
    raw?.avatar ||
    null;
  const resolvedLegacy = resolveBackendUrl(legacy);
  if (resolvedLegacy) return resolvedLegacy;
  const staffId = Number(
    raw?.staffId ?? raw?.account_id ?? raw?.staff_id ?? raw?.staffId,
  );
  if (Number.isFinite(staffId) && staffId > 0) {
    return accountAvatarImgSrc(staffId, raw) || "";
  }
  return "";
}

// Safe parse of duration / hours into a numeric hour value
export function parseHours(raw) {
  if (raw === null || raw === undefined || raw === "") return 0;
  if (typeof raw === "number") return raw;
  const s = String(raw).trim();

  // Pure numeric (possibly with "h" suffix)
  const numeric = parseFloat(s.replace(/[^0-9.]/g, ""));
  if (
    !Number.isNaN(numeric) &&
    /^(?:\d+|\d*\.\d+)$/.test(s.replace(/[^0-9.]/g, ""))
  ) {
    return numeric;
  }

  // Pattern like "7h 45m"
  const hMatch = s.match(/(\d+(?:\.\d+)?)h/);
  const mMatch = s.match(/(\d+(?:\.\d+)?)m/);
  const h = hMatch ? parseFloat(hMatch[1]) : 0;
  const m = mMatch ? parseFloat(mMatch[1]) : 0;
  if (h || m) return h + m / 60;

  return 0;
}

function normalizeRow(r) {
  const startTime =
    r.start_time ||
    r.scheduledStart ||
    r.scheduled_start ||
    r.from ||
    r.start ||
    r.startTime ||
    "";
  const endTime =
    r.end_time ||
    r.scheduledEnd ||
    r.scheduled_end ||
    r.to ||
    r.end ||
    r.endTime ||
    "";

  const locationId =
    r.location_id ||
    r.client_location_id ||
    r.clientLocationId ||
    r.locationId ||
    null;
  const locationName =
    r.clientLocationName ||
    r.location ||
    r.location_name ||
    r.address ||
    "";

  const hours =
    typeof r.hours !== "undefined" ? parseHours(r.hours) : parseHours(r.duration);

  return {
    startTime,
    endTime,
    locationId,
    locationName,
    scheduledDurationHours: hours,
    role: r.role || r.position || r.client_position || "Role",
    staffName: r.staff || r.temp || r.staffName || "",
    staffId: r.staffId ?? r.account_id ?? r.staff_id ?? null,
    avatar:
      r.avatar_url ||
      r.profile_picture ||
      r.staff_avatar ||
      r.avatar ||
      null,
    checkinTime:
      r.checkin_time ||
      r.checkin_date ||
      r.checkinDateIso ||
      r.actualStartDate ||
      r.actualStartDateIso ||
      r.actualStart ||
      r.actual_start ||
      null,
    checkoutTime:
      r.checkout_time ||
      r.checkout_date ||
      r.checkoutDateIso ||
      r.actualEndDate ||
      r.actualEndDateIso ||
      r.actualEnd ||
      r.actual_end ||
      null,
    breakMinutes:
      r.break_minutes ??
      r.breakMinutes ??
      r.actual_unpaid_break_minutes ??
      r.checkinUnpaidBreakMinutes ??
      null,
    totalAfterBreak: r.total_after_break || null,
    travelTimeAmount: r.travel_time_amount ?? r.travelTimeAmount ?? null,
    travelTimeType: r.travel_time_type ?? r.travelTimeType ?? null,
    carpoolName: r.carpool_name ?? r.carpoolName ?? null,
    carpoolNote: r.carpool_note ?? r.carpoolNote ?? null,
    supervisors:
      r.supervisors ||
      r.supervisor ||
      r.supervisorName ||
      r.shiftSupervisor ||
      null,
    staffRequestId:
      r.shiftAccountId ||
      r.shift_account_id ||
      r.shiftId ||
      r.shift_id ||
      null,
    // Preserve an identifier for this timesheet row if present in API
    timesheetId: r.timesheet_id || r.id || r.shift_account_id || r.shiftAccountId || null,
  };
}

function computeTotalAfterBreakIfMissing(position) {
  if (position.totalAfterBreak !== null && position.totalAfterBreak !== undefined) {
    return position.totalAfterBreak;
  }
  if (!position.checkinTime || !position.checkoutTime) return null;
  try {
    const ci = parseDbDateTime(position.checkinTime);
    const co = parseDbDateTime(position.checkoutTime);
    if (!ci || !co || Number.isNaN(ci.getTime()) || Number.isNaN(co.getTime())) return null;
    const breakMin = Number(position.breakMinutes || 0);
    const diffMin = Math.max(0, (co - ci) / 60000 - breakMin);
    return Number((diffMin / 60).toFixed(2));
  } catch (_) {
    return null;
  }
}

// Group flat rows into slot objects:
// [{ date, start_time, end_time, hours, clientLocationName, positions: [...] }]
export function parseTimeToMinutes(val) {
  if (!val) return null;

  // 0. Full date/datetime values (Date objects, or "YYYY-MM-DD[T ]HH:MM..." strings
  // as returned raw by the API) must be converted through the app's display
  // timezone before extracting an hour/minute — DB timestamps are UTC, and a
  // naive substring grab on a UTC string silently reads the wrong wall-clock
  // hour (e.g. an 11:30pm Eastern shift serializes as "...T03:30:00.000Z",
  // sorting it as if it started at 3:30am). Bare display strings like
  // "6.30 am - 2.30 pm" have no date/timezone component and skip this branch.
  if (val instanceof Date || /^\d{4}-\d{2}-\d{2}([T ]|$)/.test(String(val).trim())) {
    const parts = getAppWallDateParts(val);
    if (parts) return parts.hour * 60 + parts.minute;
  }

  const str = String(val || "").trim();
  if (!str) return null;

  // 1. Extract start time if range string like "4.30 pm - 12.30 am" or "6.30 am - 2.30 pm"
  let timePart = str;
  if (str.includes(" - ")) {
    timePart = str.split(/\s+-\s+/)[0].trim();
  } else if (str.includes(" to ")) {
    timePart = str.split(/\s+to\s+/i)[0].trim();
  } else if (str.includes(" – ")) {
    timePart = str.split(/\s+–\s+/)[0].trim();
  }

  // 2. Parse 12-hour or 24-hour time format e.g. "4.30 pm", "6.30 am", "16:30", "06:30"
  const timeMatch = timePart.match(/(\d{1,2})[:.](\d{2})(?::\d{2})?\s*(am|pm)?/i);
  if (timeMatch) {
    let hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10) || 0;
    const ampm = timeMatch[3]?.toLowerCase();
    if (ampm === "pm" && hours < 12) hours += 12;
    if (ampm === "am" && hours === 12) hours = 0;
    return hours * 60 + minutes;
  }

  // 3. Fallback to ISO/DB Date parsing
  try {
    const d = parseDbDateTime(str) || new Date(str);
    if (d && !Number.isNaN(d.getTime())) {
      return d.getHours() * 60 + d.getMinutes();
    }
  } catch (_) { }

  return null;
}

export function getSlotStartMinutes(slot) {
  if (!slot) return 0;

  let min = parseTimeToMinutes(slot.start_time);
  if (min !== null) return min;

  min = parseTimeToMinutes(slot.time);
  if (min !== null) return min;

  if (Array.isArray(slot.positions) && slot.positions.length > 0) {
    min = parseTimeToMinutes(slot.positions[0]?.checkin_time);
    if (min !== null) return min;
  }

  return 0;
}

export function groupTimesheetsIntoSlots(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const map = new Map();

  rows.forEach((raw) => {
    const r = normalizeRow(raw);
    if (!r.startTime && !r.endTime) return;

    // Stable date from scheduled start (local string slice avoids TZ shifts).
    const slotDate =
      (r.startTime && String(r.startTime).slice(0, 10)) ||
      (r.endTime && String(r.endTime).slice(0, 10)) ||
      "";

    const locationKey = r.locationId || r.locationName || "unknown-location";

    const key = `${slotDate}|${locationKey}|${r.startTime}|${r.endTime}`;

    if (!map.has(key)) {
      map.set(key, {
        date: slotDate,
        start_time: r.startTime,
        end_time: r.endTime,
        hours: 0,
        clientLocationName: r.locationName,
        locationId: r.locationId || null,
        supervisors: [],
        staffRequestIds: [],
        positions: [],
      });
    }

    const slot = map.get(key);
    slot.hours += Number(r.scheduledDurationHours) || 0;

    // Collect supervisors / staff request IDs at slot level (idempotent)
    if (r.supervisors) {
      const supList = Array.isArray(r.supervisors)
        ? r.supervisors
        : String(r.supervisors)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      supList.forEach((s) => {
        if (!slot.supervisors.includes(s)) slot.supervisors.push(s);
      });
    }
    if (r.staffRequestId) {
      const val = String(r.staffRequestId);
      if (!slot.staffRequestIds.includes(val)) slot.staffRequestIds.push(val);
    }

    const avatarSrc = resolveStaffAvatarSrc({
      ...raw,
      avatar_url: r.avatar,
      profile_picture: r.avatar,
      staffId: r.staffId,
      account_id: r.staffId,
    });

    const position = {
      role: r.role,
      baseRole: normalizePositionBaseLabel(r.role),
      displayRole: normalizePositionBaseLabel(r.role),
      staffLabel: normalizeStaffLabel(r.staffName),
      staff: r.staffName,
      temp: r.staffName,
      staffId: r.staffId,
      avatar_url: avatarSrc,
      profile_picture: avatarSrc,
      travel_time_amount: r.travelTimeAmount,
      travel_time_type: r.travelTimeType,
      carpool_name: r.carpoolName,
      carpool_note: r.carpoolNote,
      checkin_time: r.checkinTime,
      checkout_time: r.checkoutTime,
      break_minutes: r.breakMinutes,
      total_after_break: computeTotalAfterBreakIfMissing({
        totalAfterBreak: r.totalAfterBreak,
        checkinTime: r.checkinTime,
        checkoutTime: r.checkoutTime,
        breakMinutes: r.breakMinutes,
      }),
      // attach the original timesheet id to the position for per-timesheet actions
      timesheetId: r.timesheetId || r.timesheet_id || r.id || r.shiftAccountId || r.shift_account_id || null,
    };

    slot.positions.push(position);
  });

  const processedSlots = Array.from(map.values()).map((slot) => {
    const sortedPositions = [...(slot.positions || [])].sort((a, b) => {
      // Unassigned slots always print at the bottom of the sheet.
      const unassignedCompare =
        Number(isUnassignedStaffLabel(a.staffLabel || a.staff || a.temp)) -
        Number(isUnassignedStaffLabel(b.staffLabel || b.staff || b.temp));
      if (unassignedCompare !== 0) return unassignedCompare;
      const roleCompare = comparePositionLabels(
        a.baseRole || a.role,
        b.baseRole || b.role,
      );
      if (roleCompare !== 0) return roleCompare;
      return compareStaffLabels(
        a.staffLabel || a.staff || a.temp,
        b.staffLabel || b.staff || b.temp,
      );
    });

    const counts = new Map();
    sortedPositions.forEach((position) => {
      const baseRole = normalizePositionBaseLabel(position.baseRole || position.role);
      const nextCount = (counts.get(baseRole) || 0) + 1;
      counts.set(baseRole, nextCount);
      position.displayRole = `${baseRole} ${nextCount}`;
    });

    return {
      ...slot,
      positions: sortedPositions,
    };
  });

  // Slots with no assigned staff always print at the bottom of the sheet.
  const isUnassignedSlot = (slot) =>
    (slot.positions || []).every((p) =>
      isUnassignedStaffLabel(p.staffLabel || p.staff || p.temp),
    );

  return processedSlots.sort((a, b) => {
    const unassignedCompare =
      Number(isUnassignedSlot(a)) - Number(isUnassignedSlot(b));
    if (unassignedCompare !== 0) return unassignedCompare;

    const startA = getSlotStartMinutes(a);
    const startB = getSlotStartMinutes(b);
    if (startA !== startB) return startA - startB;

    const endA = parseTimeToMinutes(a.end_time);
    const endB = parseTimeToMinutes(b.end_time);
    if (endA !== null && endB !== null && endA !== endB) {
      return endA - endB;
    }

    const staffCompare = compareStaffLabels(
      getSlotStaffLabel(a),
      getSlotStaffLabel(b),
    );
    if (staffCompare !== 0) return staffCompare;

    return String(a.start_time || "").localeCompare(String(b.start_time || ""));
  });
}
